import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useProjects } from '../context/ProjectContext';
import { addDays } from 'date-fns';

const AIAssistant = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hi! I can help you create projects, check deadlines, or organize your workflow. Try saying "Create a new Marketing video due next Friday".' }
  ]);
  const [input, setInput] = useState('');
  const { addProject } = useProjects();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = () => {
    if (!input.trim()) return;

    const userMsg = { role: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');

    // Simulate AI processing
    setTimeout(() => {
      processCommand(input);
    }, 600);
  };

  const processCommand = (text) => {
    const lowerText = text.toLowerCase();
    
    // Simple mock NLP
    if (lowerText.includes('create') && (lowerText.includes('project') || lowerText.includes('video'))) {
      const isMarketing = lowerText.includes('marketing');
      const isSales = lowerText.includes('sales');
      const isHR = lowerText.includes('hr');
      const isRA = lowerText.includes('r&a');
      const isExCo = lowerText.includes('exco');
      const isEcoTeams = lowerText.includes('eco teams');
      const isInternalComms = lowerText.includes('internal comms');
      const isExternalComms = lowerText.includes('external comms');
      const isLeadership = lowerText.includes('leadership');

      const newProject = {
        title: 'New AI Generated Project',
        department: isMarketing ? 'Marketing' : isSales ? 'Sales' : isHR ? 'HR' : isRA ? 'R&A' : isExCo ? 'ExCo' : isEcoTeams ? 'Eco Teams' : isInternalComms ? 'Internal Comms' : isExternalComms ? 'External Comms' : isLeadership ? 'Leadership' : 'Product',
        urgency: lowerText.includes('urgent') ? 'Critical' : 'Medium',
        deliveryDate: addDays(new Date(), 7).toISOString(),
        editStartDate: new Date().toISOString(),
        finalEditDate: addDays(new Date(), 5).toISOString(),
        stakeholder: 'Pending Assignment',
        type: 'Internal',
        status: 'Active',
      };

      addProject(newProject);
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        text: `I've created a draft project for ${newProject.department} due in 7 days. You can edit the details in the Projects view.` 
      }]);
    } else if (lowerText.includes('hello') || lowerText.includes('hi')) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Hello! Ready to get creative? What are we working on today?' }]);
    } else {
      setMessages(prev => [...prev, { role: 'assistant', text: 'I didn\'t quite catch that. Try asking me to "Create a project" or "Check deadlines".' }]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-2xl shadow-2xl flex flex-col overflow-hidden h-[600px] animate-fade-in-up">
        {/* Header */}
        <div className="p-4 border-b dark:border-gray-700 bg-indigo-600 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiZap} className="text-xl" />
            <h3 className="font-semibold">Creative Assistant</h3>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition">
            <SafeIcon icon={FiIcons.FiX} />
          </button>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                msg.role === 'user' 
                  ? 'bg-indigo-600 text-white rounded-br-none' 
                  : 'bg-white dark:bg-gray-800 dark:text-gray-200 border dark:border-gray-700 rounded-bl-none shadow-sm'
              }`}>
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white dark:bg-gray-800 border-t dark:border-gray-700">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask me to create a project..."
              className="flex-1 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button 
              onClick={handleSend}
              className="bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-xl transition-colors"
            >
              <SafeIcon icon={FiIcons.FiSend} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;